// Small, dependency-free UI primitives shared by every screen.

import type { LucideIcon } from 'lucide-react';
import { Children, isValidElement, useEffect, useId, useRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';

import type { Tone } from './tone.ts';
export type { Tone };

// --- Button -----------------------------------------------------------------

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'soft' | 'ghost' | 'danger' | 'outline';
  size?: 'sm' | 'md' | 'lg';
  icon?: LucideIcon;
  block?: boolean;
}

export function Button({ variant = 'soft', size = 'md', icon: Icon, block, className = '', children, ...rest }: ButtonProps) {
  return (
    <button type="button" className={`btn btn-${variant} btn-${size}${block ? ' btn-block' : ''} ${className}`} {...rest}>
      {Icon && <Icon className="btn-icon" aria-hidden />}
      {children && <span>{children}</span>}
    </button>
  );
}

export function IconButton({ icon: Icon, label, className = '', ...rest }: ButtonHTMLAttributes<HTMLButtonElement> & { icon: LucideIcon; label: string }) {
  return (
    <button type="button" className={`icon-btn ${className}`} aria-label={label} title={label} {...rest}>
      <Icon aria-hidden />
    </button>
  );
}

// --- Card -------------------------------------------------------------------

export function Card({ children, className = '', tone, onClick }: { children: ReactNode; className?: string; tone?: Tone; onClick?: () => void }) {
  const cls = `card${tone ? ` tone-${tone}` : ''}${onClick ? ' card-click' : ''} ${className}`;
  if (onClick)
    return (
      <button type="button" className={cls} onClick={onClick}>
        {children}
      </button>
    );
  return <section className={cls}>{children}</section>;
}

export function CardHeader({ title, icon: Icon, action, sub }: { title: ReactNode; icon?: LucideIcon; action?: ReactNode; sub?: ReactNode }) {
  return (
    <header className="card-header">
      {Icon && <Icon className="card-header-icon" aria-hidden />}
      <div className="card-header-text">
        <h2>{title}</h2>
        {sub && <p className="muted small">{sub}</p>}
      </div>
      {action && <div className="card-header-action">{action}</div>}
    </header>
  );
}

// --- Page -------------------------------------------------------------------

export function Page({ title, sub, actions, children, className = '' }: { title?: ReactNode; sub?: ReactNode; actions?: ReactNode; children: ReactNode; className?: string }) {
  return (
    <div className={`page ${className}`}>
      {(title || actions) && (
        <header className="page-header">
          <div>
            {title && <h1>{title}</h1>}
            {sub && <p className="muted">{sub}</p>}
          </div>
          {actions && <div className="page-actions">{actions}</div>}
        </header>
      )}
      {children}
    </div>
  );
}

// --- Badge ------------------------------------------------------------------

export function Badge({ tone = 'neutral', children, icon: Icon }: { tone?: Tone; children: ReactNode; icon?: LucideIcon }) {
  return (
    <span className={`badge tone-${tone}`}>
      {Icon && <Icon aria-hidden />}
      {children}
    </span>
  );
}

// --- Segmented control --------------------------------------------------------

export function Segmented<T extends string | number>({
  options,
  value,
  onChange,
  size = 'md',
  label,
}: {
  options: { value: T; label: ReactNode; icon?: LucideIcon }[];
  value: T | undefined;
  onChange: (v: T) => void;
  size?: 'sm' | 'md' | 'lg';
  label?: string;
}) {
  return (
    <div className={`segmented seg-${size}`} role="radiogroup" aria-label={label}>
      {options.map((o) => (
        <button
          key={String(o.value)}
          type="button"
          role="radio"
          aria-checked={o.value === value}
          className={o.value === value ? 'on' : ''}
          onClick={() => onChange(o.value)}
        >
          {o.icon && <o.icon aria-hidden />}
          <span>{o.label}</span>
        </button>
      ))}
    </div>
  );
}

// --- Chips (multi-select) ----------------------------------------------------

export function Chips<T extends string>({ options, value, onChange }: { options: { value: T; label: string }[]; value: T[]; onChange: (v: T[]) => void }) {
  return (
    <div className="chips">
      {options.map((o) => {
        const on = value.includes(o.value);
        return (
          <button
            key={o.value}
            type="button"
            className={`chip${on ? ' on' : ''}`}
            aria-pressed={on}
            onClick={() => onChange(on ? value.filter((v) => v !== o.value) : [...value, o.value])}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

// --- Form fields ----------------------------------------------------------------

const SINGLE_CONTROLS = new Set(['input', 'select', 'textarea']);

/**
 * A labelled form row. Around a single input it's a real <label>. Around
 * anything else (segmented buttons, chips, several inputs) it's a labelled
 * group instead — a <label> would pass taps on its text to the first button
 * inside, silently choosing an option.
 */
export function Field({ label, hint, children }: { label: ReactNode; hint?: ReactNode; children: ReactNode }) {
  const id = useId();
  const only = Children.toArray(children);
  const single = only.length === 1 && isValidElement(only[0]) && typeof only[0].type === 'string' && SINGLE_CONTROLS.has(only[0].type);
  if (single)
    return (
      <label className="field">
        <span className="field-label">{label}</span>
        {children}
        {hint && <span className="field-hint">{hint}</span>}
      </label>
    );
  return (
    <div className="field" role="group" aria-labelledby={id}>
      <span className="field-label" id={id}>
        {label}
      </span>
      {children}
      {hint && <span className="field-hint">{hint}</span>}
    </div>
  );
}

// --- Sheet (modal dialog) -------------------------------------------------------

export function Sheet({
  open,
  onClose,
  title,
  children,
  footer,
  wide,
}: {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  wide?: boolean;
}) {
  const id = useId();
  const ref = useRef<HTMLDivElement>(null);
  // The latest onClose, without re-running the effects below: callers pass a new
  // function every render, and re-focusing the sheet on each keystroke pulled
  // focus out of its inputs (the phone keyboard flickered or closed).
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && closeRef.current();
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);
  // Focus the sheet once when it opens — never when something inside already has focus.
  useEffect(() => {
    if (open && !ref.current?.contains(document.activeElement)) ref.current?.focus();
  }, [open]);
  if (!open) return null;
  return createPortal(
    <div className="sheet-backdrop" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className={`sheet${wide ? ' sheet-wide' : ''}`} role="dialog" aria-modal="true" aria-labelledby={id} tabIndex={-1} ref={ref}>
        <header className="sheet-header">
          <h2 id={id}>{title}</h2>
          <IconButton icon={X} label="Close" onClick={onClose} />
        </header>
        <div className="sheet-body">{children}</div>
        {footer && <footer className="sheet-footer">{footer}</footer>}
      </div>
    </div>,
    document.body,
  );
}

// --- Progress ring ------------------------------------------------------------------

export function Ring({ value, size = 64, stroke = 7, tone = 'accent', children }: { value: number; size?: number; stroke?: number; tone?: Tone; children?: ReactNode }) {
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const v = Math.max(0, Math.min(1, value));
  return (
    <div className={`ring tone-${tone}`} style={{ width: size, height: size }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} aria-hidden>
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" strokeWidth={stroke} className="ring-track" />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          strokeWidth={stroke}
          className="ring-value"
          strokeDasharray={c}
          strokeDashoffset={c * (1 - v)}
          strokeLinecap="round"
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
        />
      </svg>
      {children && <div className="ring-label">{children}</div>}
    </div>
  );
}

export function ProgressBar({ value, tone = 'accent', label }: { value: number; tone?: Tone; label?: string }) {
  return (
    <div className={`progress tone-${tone}`} role="progressbar" aria-valuenow={Math.round(value * 100)} aria-valuemin={0} aria-valuemax={100} aria-label={label}>
      <div style={{ width: `${Math.max(0, Math.min(1, value)) * 100}%` }} />
    </div>
  );
}

// --- Empty state ---------------------------------------------------------------------

export function Empty({ icon: Icon, title, children }: { icon: LucideIcon; title: string; children?: ReactNode }) {
  return (
    <div className="empty">
      <Icon aria-hidden />
      <strong>{title}</strong>
      {children && <p className="muted">{children}</p>}
    </div>
  );
}

// --- Tabs ------------------------------------------------------------------------------

export function Tabs<T extends string>({ tabs, value, onChange }: { tabs: { value: T; label: string; icon?: LucideIcon; count?: number }[]; value: T; onChange: (v: T) => void }) {
  // On a phone the strip scrolls: keep the chosen tab in view.
  const ref = useRef<HTMLElement>(null);
  useEffect(() => {
    const on = ref.current?.querySelector<HTMLElement>('button.on');
    const strip = ref.current;
    if (on && strip && strip.scrollWidth > strip.clientWidth) strip.scrollTo({ left: on.offsetLeft - (strip.clientWidth - on.offsetWidth) / 2, behavior: 'smooth' });
  }, [value]);
  return (
    <nav className="tabs" role="tablist" ref={ref}>
      {tabs.map((t) => (
        <button key={t.value} type="button" role="tab" aria-selected={t.value === value} className={t.value === value ? 'on' : ''} onClick={() => onChange(t.value)}>
          {t.icon && <t.icon aria-hidden />}
          <span>{t.label}</span>
          {t.count !== undefined && <span className="tab-count">{t.count}</span>}
        </button>
      ))}
    </nav>
  );
}

export function Toggle({ on, onChange, label, disabled }: { on: boolean; onChange: (v: boolean) => void; label: string; disabled?: boolean }) {
  return <button type="button" role="switch" aria-checked={on} aria-label={label} className="toggle" disabled={disabled} onClick={() => onChange(!on)} />;
}
